import { NavLink, useLocation } from "react-router";
import {
  Home,
  Package,
  UtensilsCrossed,
  Trash2,
  CalendarDays,
  Settings,
  LogOut,
  ChevronRight,
  Building2,
  Users,
  Shield,
  UserCog,
  Briefcase,
  ClipboardList,
  Wrench,
  Database,
  Router,
  Activity,
  HeartPulse,
  ScrollText,
  Tags,
  Languages,
} from "lucide-react";
import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";
import { useClaims, hasAnyPermission } from "@/hooks/use-claims";
import { useTranslation } from "@/i18n/use-translation";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useBrandQuery } from "@/hooks/use-brand";
import { ContextPicker } from "@/components/layout/context-picker";
import foodsightIcon from "@/assets/foodsight-icon.svg";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Collapsible } from "radix-ui";

// Keys, not strings: a label resolved here would capture the locale at import time and never
// change again (POS2-1133). Every one is translated at render, inside the component.
const NAV_ITEMS = [
  { labelKey: "nav.dashboard", icon: Home, path: "/" },
  { labelKey: "nav.stock", icon: Package, path: "/stock" },
  // One item: recipes are reached from the Menus page (POS2-1456).
  { labelKey: "nav.menus", icon: UtensilsCrossed, path: "/menus" },
] as const;

const NAV_GROUPS = [
  {
    labelKey: "nav.waste",
    icon: Trash2,
    items: [
      { labelKey: "nav.transactions", path: "/waste/transactions" },
      { labelKey: "nav.reports", path: "/waste/reports" },
      { labelKey: "nav.explore", path: "/waste/explore" },
      { labelKey: "nav.summary", path: "/waste/summary", badgeKey: "common.comingSoon" },
    ],
  },
] as const;

// Rendered after NAV_GROUPS rather than folded into NAV_ITEMS: the spec places Occupancy after
// the Waste group, and NAV_ITEMS renders above the groups (POS2-1259).
const NAV_ITEMS_AFTER_GROUPS = [
  { labelKey: "nav.occupancy", icon: CalendarDays, path: "/occupancy" },
] as const;

export function AppSidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { isPlatformAdmin, orgId: claimsOrgId, permissions } = useClaims();
  const adminOrgId = useAdminOrgStore((s) => s.orgId);
  // The same query the shell's brand hook reads — one key, so one fetch.
  const brand = useBrandQuery();
  const showOrgAdmin =
    hasAnyPermission(permissions, ["user:invite", "user:update", "role:create"]) ||
    (isPlatformAdmin && !!adminOrgId);
  const showContextPicker = isPlatformAdmin || !!claimsOrgId;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        {/* The one place a logo is rendered in the whole app (POS2-1132). An organisation with
            no logo keeps the FoodSight mark and wordmark, which is every organisation today. */}
        <div className="flex h-8 items-center gap-2 px-2">
          <img
            src={brand.data?.logo_url ?? foodsightIcon}
            alt={brand.data?.logo_url ? (brand.data.name ?? t("nav.organisation")) : "FoodSight"}
            className="size-6 shrink-0 object-contain"
          />
          <span className="truncate text-sm font-semibold group-data-[collapsible=icon]:hidden">
            {brand.data?.logo_url ? brand.data.name : "FoodSight"}
          </span>
        </div>
        {showContextPicker && <ContextPicker />}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV_ITEMS.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    asChild
                    isActive={
                      location.pathname === item.path ||
                      location.pathname.startsWith(`${item.path}/`)
                    }
                    tooltip={t(item.labelKey)}
                  >
                    <NavLink to={item.path}>
                      <item.icon />
                      <span>{t(item.labelKey)}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {NAV_GROUPS.map((group) => {
                const isGroupActive = group.items.some((item) =>
                  location.pathname.startsWith(item.path),
                );
                return (
                  <Collapsible.Root
                    key={group.labelKey}
                    defaultOpen={isGroupActive}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <Collapsible.Trigger asChild>
                        <SidebarMenuButton tooltip={t(group.labelKey)} isActive={isGroupActive}>
                          <group.icon />
                          <span>{t(group.labelKey)}</span>
                          <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </Collapsible.Trigger>
                      <Collapsible.Content>
                        <SidebarMenuSub>
                          {group.items.map((item) => {
                            // Every badge in this group is now static (POS2-1249). Reports carried
                            // the one per-viewer branch, and un-gating deleted it along with the
                            // access rule it read.
                            const badgeKey = "badgeKey" in item ? item.badgeKey : null;
                            const badge = badgeKey ? t(badgeKey) : null;
                            return (
                              <SidebarMenuSubItem key={item.path}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={location.pathname === item.path}
                                >
                                  <NavLink to={item.path}>
                                    <span>{t(item.labelKey)}</span>
                                    {badge && (
                                      <Badge
                                        variant="secondary"
                                        className="ml-auto shrink-0 px-1.5 py-0 text-[10px] font-medium"
                                      >
                                        {badge}
                                      </Badge>
                                    )}
                                  </NavLink>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </Collapsible.Content>
                    </SidebarMenuItem>
                  </Collapsible.Root>
                );
              })}

              {NAV_ITEMS_AFTER_GROUPS.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname === item.path}
                    tooltip={t(item.labelKey)}
                  >
                    <NavLink to={item.path}>
                      <item.icon />
                      <span>{t(item.labelKey)}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {showOrgAdmin && (
          <Collapsible.Root defaultOpen className="group/org-admin">
            <SidebarGroup>
              <Collapsible.Trigger asChild>
                <SidebarGroupLabel className="cursor-pointer select-none font-semibold">
                  {t("nav.organisation")}
                  <ChevronRight className="ml-auto size-3 transition-transform group-data-[state=open]/org-admin:rotate-90" />
                </SidebarGroupLabel>
              </Collapsible.Trigger>
              <Collapsible.Content>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/settings/organisation"}
                        tooltip={t("nav.organisation")}
                      >
                        <NavLink to="/settings/organisation">
                          <Building2 />
                          <span>{t("nav.overview")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/settings/users"}
                        tooltip={t("nav.usersAndRoles")}
                      >
                        <NavLink to="/settings/users">
                          <UserCog />
                          <span>{t("nav.usersAndRoles")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/settings/accounts"}
                        tooltip={t("nav.accounts")}
                      >
                        <NavLink to="/settings/accounts">
                          <Briefcase />
                          <span>{t("nav.accounts")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {isPlatformAdmin && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          asChild
                          isActive={location.pathname === "/settings/catalogue"}
                          tooltip={t("nav.catalogue")}
                        >
                          <NavLink to="/settings/catalogue">
                            <ClipboardList />
                            <span>{t("nav.catalogue")}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                    <Collapsible.Root
                      defaultOpen={[
                        "/settings/sites",
                        "/settings/kitchens",
                        "/settings/devices",
                      ].some((p) => location.pathname === p)}
                      className="group/infra"
                    >
                      <SidebarMenuItem>
                        <Collapsible.Trigger asChild>
                          <SidebarMenuButton
                            tooltip={t("nav.infrastructure")}
                            isActive={[
                              "/settings/sites",
                              "/settings/kitchens",
                              "/settings/devices",
                            ].some((p) => location.pathname === p)}
                          >
                            <Wrench />
                            <span>{t("nav.infrastructure")}</span>
                            <ChevronRight className="ml-auto transition-transform group-data-[state=open]/infra:rotate-90" />
                          </SidebarMenuButton>
                        </Collapsible.Trigger>
                        <Collapsible.Content>
                          <SidebarMenuSub>
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={location.pathname === "/settings/sites"}
                              >
                                <NavLink to="/settings/sites">
                                  <span>{t("nav.sites")}</span>
                                </NavLink>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={location.pathname === "/settings/kitchens"}
                              >
                                <NavLink to="/settings/kitchens">
                                  <span>{t("nav.kitchens")}</span>
                                </NavLink>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton
                                asChild
                                isActive={location.pathname === "/settings/devices"}
                              >
                                <NavLink to="/settings/devices">
                                  <span>{t("nav.devices")}</span>
                                </NavLink>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          </SidebarMenuSub>
                        </Collapsible.Content>
                      </SidebarMenuItem>
                    </Collapsible.Root>
                  </SidebarMenu>
                </SidebarGroupContent>
              </Collapsible.Content>
            </SidebarGroup>
          </Collapsible.Root>
        )}

        {isPlatformAdmin && (
          <Collapsible.Root defaultOpen className="group/platform-admin">
            <SidebarGroup>
              <Collapsible.Trigger asChild>
                <SidebarGroupLabel className="cursor-pointer select-none font-semibold">
                  {t("nav.platformAdmin")}
                  <ChevronRight className="ml-auto size-3 transition-transform group-data-[state=open]/platform-admin:rotate-90" />
                </SidebarGroupLabel>
              </Collapsible.Trigger>
              <Collapsible.Content>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/organisations"}
                        tooltip={t("nav.organisations")}
                      >
                        <NavLink to="/admin/organisations">
                          <Building2 />
                          <span>{t("nav.organisations")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/users"}
                        tooltip={t("nav.platformUsers")}
                      >
                        <NavLink to="/admin/users">
                          <Shield />
                          <span>{t("nav.platformUsers")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/sso"}
                        tooltip={t("nav.ssoProviders")}
                      >
                        <NavLink to="/admin/sso">
                          <Users />
                          <span>{t("nav.ssoProviders")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/catalogues"}
                        tooltip={t("nav.catalogues")}
                      >
                        <NavLink to="/admin/catalogues">
                          <Package />
                          <span>{t("nav.catalogues")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/default-menu"}
                        tooltip={t("nav.defaultMenu")}
                      >
                        <NavLink to="/admin/default-menu">
                          <UtensilsCrossed />
                          <span>{t("nav.defaultMenu")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/databridge"}
                        tooltip={t("nav.dataBridge")}
                      >
                        <NavLink to="/admin/databridge">
                          <Database />
                          <span>{t("nav.dataBridge")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/devices"}
                        tooltip={t("nav.devices")}
                      >
                        <NavLink to="/admin/devices">
                          <Router />
                          <span>{t("nav.devices")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/sites"}
                        tooltip={t("nav.sites")}
                      >
                        <NavLink to="/admin/sites">
                          <Building2 />
                          <span>{t("nav.sites")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/monitoring"}
                        tooltip={t("nav.monitoring")}
                      >
                        <NavLink to="/admin/monitoring">
                          <Activity />
                          <span>{t("nav.monitoring")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/labelling"}
                        tooltip={t("nav.labelling")}
                      >
                        <NavLink to="/admin/labelling">
                          <Tags />
                          <span>{t("nav.labelling")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/activity"}
                        tooltip={t("nav.activity")}
                      >
                        <NavLink to="/admin/activity">
                          <ScrollText />
                          <span>{t("nav.activity")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/customer-health"}
                        tooltip={t("nav.customerHealth")}
                      >
                        <NavLink to="/admin/customer-health">
                          <HeartPulse />
                          <span>{t("nav.customerHealth")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === "/admin/translations"}
                        tooltip={t("nav.translations")}
                      >
                        <NavLink to="/admin/translations">
                          <Languages />
                          <span>{t("nav.translations")}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </Collapsible.Content>
            </SidebarGroup>
          </Collapsible.Root>
        )}
      </SidebarContent>
      <SidebarSeparator className="group-data-[collapsible=icon]:hidden" />
      <SidebarFooter>
        <UserAccountFooter />
      </SidebarFooter>
    </Sidebar>
  );
}

function UserAccountFooter() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-2 group-data-[collapsible=icon]:justify-center">
        <span className="truncate text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
          {user?.email}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          onClick={() => setOpen(true)}
          aria-label={t("nav.accountSettings")}
        >
          <Settings className="size-4" />
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("nav.account")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-1">
            <div className="rounded-md px-3 py-2">
              <p className="text-sm font-medium">{user?.user_metadata?.full_name ?? user?.email}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <NavLink
              to="/account"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-muted"
            >
              <UserCog className="size-4 text-muted-foreground" />
              {t("nav.profile")}
            </NavLink>
            <NavLink
              to="/account/security"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-muted"
            >
              <Shield className="size-4 text-muted-foreground" />
              {t("nav.security")}
            </NavLink>
            <button
              onClick={() => {
                setOpen(false);
                supabase.auth.signOut();
              }}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-red-600 hover:bg-muted"
            >
              <LogOut className="size-4" />
              {t("nav.signOut")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
